import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "../../api";
import {
  FaCalendarAlt, FaMapMarkerAlt, FaBoxOpen, FaUserTie, FaCommentDots,
  FaFilePdf, FaCreditCard, FaTimes, FaTruckLoading, FaCheck, FaChevronDown, FaEdit,
} from "react-icons/fa";

const euro = (v) =>
  (Number(v) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

const safeNumber = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

function RecapItem({ icon, label, value }) {
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-base-200 bg-base-200/40 px-3 py-2.5">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-base-content/40">{label}</p>
        <p className="truncate text-sm font-bold text-base-content">{value}</p>
      </div>
    </div>
  );
}

// Page détail — remplace le "toast + rien" affiché jusqu'ici au clic sur un devis
// validé. Toutes les infos utiles (récap, paiement, logistique, prestations) sont
// visibles directement ici, sans passer par le PDF. Permet aussi de (dé)sous-traiter
// la 1re prestation même devis validé (endpoint dédié, hors verrou anti-fraude).
export default function QuoteDetailView({ quoteId, onClose, onChanged, onEdit }) {
  const { t } = useTranslation();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subcontractors, setSubcontractors] = useState([]);

  const [providerType, setProviderType] = useState("internal");
  const [subcontractorId, setSubcontractorId] = useState(null);
  const [savingProvider, setSavingProvider] = useState(false);
  const [pdfMenuOpen, setPdfMenuOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/quote/${quoteId}`);
      setQuote(data);
      const prestations = Array.isArray(data.prestations) ? data.prestations : [];
      const svcLine = prestations.find((p) => p?.__fixed === "SERVICE") || prestations[0];
      setProviderType(svcLine?.provider_type === "subcontractor" ? "subcontractor" : "internal");
      setSubcontractorId(svcLine?.subcontractor_id ?? null);
    } catch {
      toast.error(t("quote.fetchError", "Erreur de chargement du devis"));
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/subcontractors").then((r) => setSubcontractors(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId]);

  const saveProvider = async () => {
    if (providerType === "subcontractor" && !subcontractorId) {
      toast.error(t("quote.selectSubcontractor", "Choisir un sous-traitant…"));
      return;
    }
    setSavingProvider(true);
    try {
      await api.put(`/quote/${quoteId}/provider`, {
        provider_type: providerType,
        subcontractor_id: providerType === "subcontractor" ? subcontractorId : null,
      });
      toast.success(t("quote.providerSaved", "Prestataire mis à jour."));
      onChanged?.();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || t("quote.saveError"));
    } finally {
      setSavingProvider(false);
    }
  };

  const downloadPDF = async (mode) => {
    setPdfMenuOpen(false);
    try {
      const res = await api.get(`/quote/generate/${quoteId}`, {
        responseType: "blob",
        params: mode === "ht" ? { ht: "1" } : undefined,
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error(t("quote.pdfError", "Erreur lors du téléchargement du PDF."));
    }
  };

  const statusBadgeClass = {
    pending: "bg-amber-500/10 text-amber-500 border-amber-500/20",
    validated: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
    canceled: "bg-rose-500/10 text-rose-500 border-rose-500/20",
    "follow-up": "bg-sky-500/10 text-sky-500 border-sky-500/20",
    refused: "bg-violet-500/10 text-violet-500 border-violet-500/20",
  }[quote?.status] || "bg-base-300 text-base-content/50 border-base-300";

  const logistics = quote?.logistics || {};
  const prestations = Array.isArray(quote?.prestations) ? quote.prestations : [];
  const inventory = Array.isArray(quote?.inventory) ? quote.inventory : [];
  const destination = logistics.delivery_city || logistics.delivery_zip
    ? [logistics.delivery_city, logistics.delivery_zip].filter(Boolean).join(" ")
    : (quote?.client_city || "—");

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm p-4 md:p-8 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-[96vw] max-w-[900px] bg-base-100 rounded-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col border border-base-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-base-100/95 backdrop-blur border-b border-base-200 px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <h2 className="text-base font-semibold text-base-content truncate">
              {quote?.client_name || t("quote.detailTitle", "Détail du devis")}
            </h2>
            {quote?.status && (
              <span className={`badge border ${statusBadgeClass} font-semibold`}>
                {t(`quote.statusOptions.${quote.status}`, quote.status)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Éditer : caché si devis validé (verrou anti-fraude, cohérent avec la liste) */}
            {quote && quote.status !== "validated" && onEdit && (
              <button onClick={() => onEdit(quote)} className="btn btn-ghost btn-sm gap-2">
                <FaEdit /> {t("quote.edit", "Modifier")}
              </button>
            )}
            <div className="relative">
              <button onClick={() => setPdfMenuOpen((v) => !v)} className="btn btn-ghost btn-sm gap-2">
                <FaFilePdf /> {t("quote.downloadPdf", "PDF")} <FaChevronDown className="text-[10px]" />
              </button>
              {pdfMenuOpen && (
                <div className="absolute right-0 mt-2 w-44 bg-base-100 border border-base-200 rounded-xl shadow-xl py-1 animate-fade-in z-20">
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-base-200 text-sm text-base-content/70 hover:text-base-content transition-colors"
                    onClick={() => downloadPDF("vat")}
                  >
                    {t("quote.pdf.withVAT", "Avec TVA (TTC)")}
                  </button>
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-base-200 text-sm text-base-content/70 hover:text-base-content transition-colors"
                    onClick={() => downloadPDF("ht")}
                  >
                    {t("quote.pdf.withoutVAT", "Sans TVA (HT)")}
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-base-content/40 hover:text-base-content hover:bg-base-200 transition-colors"
              aria-label="Close"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-5 space-y-5">
          {loading || !quote ? (
            <div className="flex items-center justify-center py-16">
              <span className="loading loading-spinner loading-lg text-primary" />
            </div>
          ) : (
            <>
              {/* Bannière commentaires */}
              {(logistics.comments || "").trim() && (
                <div className="flex items-start gap-2.5 rounded-xl border border-amber-300/60 bg-amber-50 px-4 py-3 text-amber-900">
                  <FaCommentDots className="mt-0.5 shrink-0 text-amber-500" />
                  <div className="min-w-0">
                    <p className="text-xs font-bold uppercase tracking-wide text-amber-600">
                      {t("quote.commentsAlert", "Commentaire important")}
                    </p>
                    <p className="text-sm whitespace-pre-line">{logistics.comments}</p>
                  </div>
                </div>
              )}

              {/* Récap */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <RecapItem icon={<FaCalendarAlt />} label={t("quote.recapDate", "Date prévue")}
                  value={quote.date ? new Date(quote.date).toLocaleDateString("fr-FR") : "—"} />
                <RecapItem icon={<FaMapMarkerAlt />} label={t("quote.recapDestination", "Destination")} value={destination} />
                <RecapItem icon={<FaBoxOpen />} label={t("quote.recapVolume", "Volume")}
                  value={`${safeNumber(logistics.volume).toFixed(2)} m³`} />
                <RecapItem icon={<FaUserTie />} label={t("quote.recapCommercial", "Commercial")}
                  value={quote.commercial_name || "—"} />
              </div>

              {/* Client + paiement */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-xl border border-base-200 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-base-content/40 mb-2">
                    {t("quote.clientName", "Client")}
                  </p>
                  <p className="text-sm font-bold text-base-content">{quote.client_name}</p>
                  <p className="text-sm text-base-content/60">{quote.client_address}, {quote.client_city} {quote.client_zip}</p>
                  {quote.client_phone && <p className="text-sm text-base-content/60">{quote.client_phone}</p>}
                  {quote.client_email && <p className="text-sm text-base-content/60">{quote.client_email}</p>}
                </div>
                <div className="rounded-xl border border-base-200 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-base-content/40 mb-2 flex items-center gap-1.5">
                    <FaCreditCard /> {t("quote.paymentMethod", "Moyen de paiement")}
                  </p>
                  <p className="text-sm font-bold text-base-content">
                    {quote.payment_method || t("quote.paymentMethodNone", "Non renseigné")}
                  </p>
                  <p className="text-xs text-base-content/40 mt-2">
                    {t("quote.prestationType", "Type de prestation")}: <span className="text-base-content/70">{quote.prestation_type}</span>
                  </p>
                </div>
              </div>

              {/* Prestataire (interne / sous-traité) — modifiable même validé */}
              <div className="rounded-xl border border-base-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-base-content/40 mb-3 flex items-center gap-1.5">
                  <FaTruckLoading /> {t("quote.providerLabel", "Réalisé par")}
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex rounded-lg overflow-hidden border border-base-300 text-sm font-medium">
                    <button
                      type="button"
                      onClick={() => setProviderType("internal")}
                      className={`px-3 py-1.5 transition-colors ${providerType === "internal" ? "bg-primary text-primary-content" : "bg-base-100 text-base-content/60 hover:bg-base-200"}`}
                    >
                      {t("quote.providerInternal", "Équipe interne")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setProviderType("subcontractor")}
                      className={`px-3 py-1.5 transition-colors border-l border-base-300 ${providerType === "subcontractor" ? "bg-orange-500 text-white" : "bg-base-100 text-base-content/60 hover:bg-base-200"}`}
                    >
                      {t("quote.providerSubcontractor", "Sous-traité")}
                    </button>
                  </div>
                  {providerType === "subcontractor" && (
                    <select
                      value={subcontractorId || ""}
                      onChange={(e) => setSubcontractorId(e.target.value ? Number(e.target.value) : null)}
                      className="select select-bordered select-sm bg-base-200 border-base-300 min-w-[200px]"
                    >
                      <option value="">{t("quote.selectSubcontractor", "Choisir un sous-traitant…")}</option>
                      {subcontractors.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  )}
                  <button
                    onClick={saveProvider}
                    disabled={savingProvider}
                    className="btn btn-primary btn-sm gap-2"
                  >
                    <FaCheck size={12} /> {savingProvider ? t("quote.saving", "Enregistrement…") : t("buttons.save", "Enregistrer")}
                  </button>
                </div>
              </div>

              {/* Prestations */}
              <div className="rounded-xl border border-base-200 overflow-hidden">
                <p className="text-xs font-semibold uppercase tracking-wide text-base-content/40 px-4 py-3 bg-base-200/50">
                  {t("quote.standardLines", "Prestations")}
                </p>
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-base-200">
                    {prestations.map((p, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2 text-base-content">{p.description}</td>
                        <td className="px-4 py-2 text-right text-base-content/60 w-20">×{p.quantite ?? 1}</td>
                        <td className="px-4 py-2 text-right font-semibold text-base-content w-28">{euro(p.total ?? p.prix)}</td>
                      </tr>
                    ))}
                  </tbody>
                  {(() => {
                    // totalHT/TTC = NET (remise déduite) ; totalFull* = brut.
                    const hasDiscount = Number(quote.discount_percent) > 0;
                    const grossHT  = Number(quote.totalFullHT  ?? quote.totalHT  ?? 0);
                    const grossTTC = Number(quote.totalFullTTC ?? quote.totalTTC ?? 0);
                    const netHT  = Number(quote.totalHT  ?? 0);
                    const netTTC = Number(quote.totalTTC ?? 0);
                    return (
                  <tfoot>
                    <tr className="border-t-2 border-base-200 bg-base-200/30">
                      <td className="px-4 py-2 font-bold text-base-content" colSpan={2}>{t("quote.totalHT", "Total HT")}</td>
                      <td className="px-4 py-2 text-right font-bold text-base-content">{euro(hasDiscount ? grossHT : netHT)}</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-1 text-base-content/50" colSpan={2}>{t("quote.tva", "TVA")}</td>
                      <td className="px-4 py-1 text-right text-base-content/50">{euro(hasDiscount ? grossTTC - grossHT : quote.totalTVA)}</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 font-bold text-primary" colSpan={2}>{t("quote.totalTTC", "Total TTC")}</td>
                      <td className="px-4 py-2 text-right font-bold text-primary">{euro(hasDiscount ? grossTTC : netTTC)}</td>
                    </tr>
                    {hasDiscount && (
                      <>
                        <tr className="border-t border-dashed border-base-300">
                          <td className="px-4 py-1 text-emerald-600" colSpan={2}>
                            {t("quote.discountAmount", "Remise")} ({Number(quote.discount_percent)}%)
                          </td>
                          <td className="px-4 py-1 text-right text-emerald-600">
                            − {euro(grossTTC - netTTC)}
                          </td>
                        </tr>
                        <tr>
                          <td className="px-4 py-1 font-semibold text-base-content" colSpan={2}>{t("quote.finalAmountHT", "Net HT")}</td>
                          <td className="px-4 py-1 text-right font-semibold text-base-content">{euro(netHT)}</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-2 font-bold text-primary" colSpan={2}>{t("quote.finalAmount", "Net à payer TTC")}</td>
                          <td className="px-4 py-2 text-right font-bold text-primary">{euro(netTTC)}</td>
                        </tr>
                      </>
                    )}
                  </tfoot>
                    );
                  })()}
                </table>
              </div>

              {/* Inventaire */}
              {inventory.length > 0 && (
                <div className="rounded-xl border border-base-200 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-base-content/40 mb-2">
                    {t("quote.inventory", "Inventaire")}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {inventory.map((it, i) => (
                      <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-base-200 text-base-content/70">
                        {it.name} ×{it.quantity}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
